import { useState, useEffect, useRef } from "react";
import React from "react";
import SbuxEnhancedTable from "../../../../../components/SbuxTable/SbuxEnhancedTable";
import SbuxTranslate from "../../../../../components/SbuxTranslate";
import MainHoursConfigTableRow from "../mainHours/MainHoursConfigTableRow";
import { detailColumns } from "./columns";
import type { HoursConfigRow } from "./types";

type DataRecord = Record<string, unknown>;

interface RowData {
  hoursConfig?: Record<string, unknown>[];
  [key: string]: unknown;
}

interface Props {
  rowData: RowData;
  memoDataset: (data: Record<string, unknown>) => void;
  isLoading?: boolean;
  isTableDisabled?: boolean;
}

const MainHoursConfigTable = ({
  rowData,
  memoDataset,
  isLoading = false,
  isTableDisabled = false,
}: Props) => {
  const [hoursConfig, setHoursConfig] = useState<HoursConfigRow[]>([]);
  const isInitialMount = useRef(true);
  const prevHoursConfigRef = useRef<Record<string, unknown>[] | undefined>(
    undefined,
  );

  const updateHoursConfigState = (
    config: HoursConfigRow[],
    skipMemoDataset = false,
  ) => {
    setHoursConfig(config);
    if (!skipMemoDataset) {
      memoDataset({ hoursConfig: config });
    }
  };

  useEffect(() => {
    // Only process external rowData changes on initial mount or when rowData.hoursConfig actually changes
    const prevConfig = prevHoursConfigRef.current;
    const newConfig = rowData.hoursConfig;

    // Deep compare to avoid unnecessary updates
    const configChanged =
      JSON.stringify(prevConfig) !== JSON.stringify(newConfig);

    if (isInitialMount.current || (configChanged && newConfig !== undefined)) {
      isInitialMount.current = false;
      prevHoursConfigRef.current = newConfig;
      const result = handleRowDataUpdate(newConfig);
      // Skip memoDataset on initial load to prevent loop
      updateHoursConfigState(result, true);
    }
  }, [rowData.hoursConfig]);

  const handleRowDataUpdate = (
    config?: Record<string, unknown>[],
  ): HoursConfigRow[] => {
    return config && config.length > 0
      ? config.map((cur, index) => ({
          ...(cur as HoursConfigRow),
          id: index,
        }))
      : [];
  };

  const handleButtonClick = () => {
    if (isTableDisabled) return;

    const rows = [...hoursConfig];
    const rowCount = rows.length;
    const initializeHoursConfig: HoursConfigRow = {
      id: rowCount,
      day: "sunday",
      start: "09:00",
      end: "17:00",
      description: "",
      isOpenFor24Hours: false,
    };

    rows.push(initializeHoursConfig);
    updateHoursConfigState(rows);
  };

  const handleDeleteRowClick = (row: DataRecord) => {
    if (isTableDisabled) return;
    const typedRow = row as HoursConfigRow;
    const filteredRows = hoursConfig.filter((f) => f.id !== typedRow.id);
    updateHoursConfigState(filteredRows);
  };

  const handleEditRowClick = (row: DataRecord) => {
    if (isTableDisabled) return;
    const typedRow = row as HoursConfigRow;
    const rows = hoursConfig.slice();
    const rowIndex = rows.findIndex((r) => r.id === typedRow.id);
    if (rowIndex !== -1) {
      rows[rowIndex] = { ...typedRow };
      updateHoursConfigState(rows);
    }
  };

  return (
    <SbuxEnhancedTable
      rows={hoursConfig as DataRecord[]}
      columns={detailColumns}
      defaultSortingField={""}
      defaultSortOrder={"asc"}
      RowSource={MainHoursConfigTableRow}
      showFooter={false}
      showSearchBar={true}
      showAddNewButton
      addNewButtonTitle={<SbuxTranslate>{"Add New hours"}</SbuxTranslate>}
      handleAddNewButtonClick={handleButtonClick}
      showModalTable
      isManager
      isTranslationRequired={true}
      handleDeleteRowClick={handleDeleteRowClick}
      handleEditRowClick={handleEditRowClick}
      isLoading={isLoading}
      isTableDisabled={isTableDisabled}
    />
  );
};

export default MainHoursConfigTable;
